import BlockIcon from '@material-design-icons/svg/filled/block.svg?react';
import CheckCircleIcon from '@material-design-icons/svg/filled/check_circle.svg?react';
import CloseIcon from '@material-design-icons/svg/filled/close.svg?react';
import EditIcon from '@material-design-icons/svg/filled/edit.svg?react';
import type { UserDetail } from '@src/dto/UserManagement';
import { useStore } from '@tanstack/react-store';
import type { SortingState } from '@tanstack/react-table';
import { use } from 'react';
import { toast } from 'sonner';
import { DataTable } from '../components/common/DataTable';
import { FilterBar, SelectFilter } from '../components/common/FilterBar';
import { Pagination } from '../components/common/Pagination';
import { RoleChangeModal } from '../components/common/RoleChangeModal';
import { ManagementPageLayout } from '../components/management/ManagementPageLayout';
import { TRPCContext } from '../contexts/TRPC';
import { AuthStateType } from '../hooks/useAuth';
import { useManagementCrud } from '../hooks/useManagementCrud';
import { usePageTitle } from '../hooks/usePageTitle';
import type { QueryLoadContext } from '../hooks/usePaginatedQueryLoader';
import { useModeratorPresets } from '../hooks/useModeratorPresets';
import { useRequireAuth } from '../hooks/useRequireAuth';
import { formatRelativeTime } from '../lib/formatters';
import { isLeftResult, isRightResult } from '../lib/trpcTypeGuards';
import {
  selectUser,
  setLoading,
  setPage,
  setPaginationInfo,
  setRoleChangeModalOpen,
  setSorting,
  setUsers,
  updateFilter,
  userManagementStore,
} from '../stores/userManagementStore';
import { captureAndReportError } from '../utils/errorReporting';
import {
  canAccessAdminPages,
  canModifyUserRole,
  type UserRole,
} from '../utils/roleUtils';
import { formatDateTimeWithLocalZone } from '../utils/stringHelpers';

const USER_KYB_STATUS_BADGE_CLASSES: Record<string, string> = {
  NotStarted: 'bg-neutral-500/10 text-neutral-400 border-neutral-500/20',
  InProgress: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  UnderReview: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  Approved: 'bg-green-500/10 text-green-400 border-green-500/20',
  Rejected: 'bg-red-500/10 text-red-400 border-red-500/20',
  RequiresAdditionalInfo:
    'bg-orange-500/10 text-orange-400 border-orange-500/20',
};

const getKybStatusBadgeColor = (kybStatus: UserDetail['kybStatus']) => {
  if (!kybStatus) return '';
  return (
    USER_KYB_STATUS_BADGE_CLASSES[kybStatus] ??
    'bg-neutral-500/10 text-neutral-300 border-neutral-500/20'
  );
};

const USER_ROLE_BADGE_CLASSES: Record<string, string> = {
  superadmin: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  admin: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  moderator: 'bg-teal-500/10 text-teal-400 border-teal-500/20',
  base: 'bg-neutral-500/10 text-neutral-400 border-neutral-500/20',
};

const getRoleBadgeColor = (role: UserDetail['role']) =>
  USER_ROLE_BADGE_CLASSES[role] ??
  'bg-neutral-500/10 text-neutral-300 border-neutral-500/20';

const formatKybStatus = (kybStatus: UserDetail['kybStatus']) => {
  if (!kybStatus) return '—';
  return kybStatus.replace(/([A-Z])/g, ' $1').trim();
};

interface UserManagementLoadContext {
  filters: {
    role: string;
    onboardingState: string;
    emailVerified: string;
    search: string;
  };
  sorting: SortingState;
  pagination: {
    currentPage: number;
    totalPages: number;
    total: number;
  };
}

export function UserManagementPage() {
  const { authState, canRender } = useRequireAuth({
    requireRole: 'admin',
  });
  const trpc = use(TRPCContext);
  usePageTitle('User Management | Publishing Portal');

  const users = useStore(userManagementStore, (state) => state.users);
  const selectedUserId = useStore(
    userManagementStore,
    (state) => state.ui.selectedUserId,
  );
  const roleChangeModalOpen = useStore(
    userManagementStore,
    (state) => state.ui.roleChangeModalOpen,
  );

  const presets = useModeratorPresets({ enabled: canRender, trpc });

  const selectedUser = users.find((u) => u.id === selectedUserId) || null;
  const viewerRole: UserRole =
    authState.type === AuthStateType.Authenticated &&
    (authState.user.role === 'admin' || authState.user.role === 'superadmin')
      ? authState.user.role
      : 'admin';

  const loadUsers = async ({
    isCurrentRequest,
    filters,
    sorting,
    pagination,
  }: QueryLoadContext & UserManagementLoadContext) => {
    try {
      const result = await trpc.client.listUsers.query({
        filters: {
          role:
            filters.role === 'all'
              ? undefined
              : (filters.role as 'base' | 'moderator' | 'admin' | 'superadmin'),
          onboardingState:
            filters.onboardingState === 'all'
              ? undefined
              : (filters.onboardingState as
                  | 'EmailVerification'
                  | 'ProfileCreation'
                  | 'KYB'
                  | 'Complete'),
          emailVerified:
            filters.emailVerified === 'all'
              ? undefined
              : (filters.emailVerified as 'true' | 'false'),
          search: filters.search || undefined,
        },
        pagination: {
          page: pagination.currentPage,
          limit: 20,
        },
        sorting:
          sorting.length > 0
            ? sorting.map((s) => ({
                id: s.id as 'created' | 'updated' | 'role' | 'emailVerified',
                desc: s.desc ?? false,
              }))
            : undefined,
      });

      if (!isCurrentRequest()) {
        return;
      }

      if (isRightResult(result)) {
        setUsers(result.right.users);
        setPaginationInfo(
          pagination.currentPage,
          result.right.pagination.totalPages,
          result.right.pagination.total,
        );
      } else if (isLeftResult(result)) {
        toast.error(result.left.message);
      }
    } catch (error) {
      if (!isCurrentRequest()) {
        return;
      }

      captureAndReportError(error, {
        operation: 'superadmin.loadUsers',
        component: 'UserManagementPage',
        filters: {
          roleFilter: filters.role,
          onboardingFilter: filters.onboardingState,
          verifiedFilter: filters.emailVerified,
          searchQuery: filters.search,
        },
        page: pagination.currentPage,
      });
      toast.error('Failed to load users');
    }
  };

  const {
    filters,
    sorting,
    pagination,
    isLoading,
    reload: reloadUsers,
  } = useManagementCrud({
    enabled: canRender,
    store: userManagementStore,
    actions: {
      updateFilter,
      setSorting,
      setPage,
      setPaginationInfo,
      setLoading,
    },
    loadPage: loadUsers,
  });

  const handleRoleUpdate = async (
    newRole: UserRole,
    presetId?: string | null,
  ) => {
    if (!selectedUser) return;
    if (!canModifyUserRole(selectedUser.role, viewerRole)) {
      toast.error("You cannot modify this user's role");
      return;
    }

    try {
      const roleChanged = newRole !== selectedUser.role;
      const presetChanged =
        presetId !== undefined &&
        presetId !== (selectedUser.moderatorPresetId ?? null);

      if (roleChanged) {
        const result = await trpc.client.updateUserRole.mutate({
          userId: selectedUser.id,
          newRole,
        });
        if (isLeftResult(result)) {
          toast.error(result.left.message);
          return;
        }
      }

      if (presetChanged && newRole === 'moderator' && presetId !== undefined) {
        const result = await trpc.client.assignModeratorPreset.mutate({
          userId: selectedUser.id,
          presetId,
        });
        if (isLeftResult(result)) {
          toast.error(result.left.message || 'Failed to assign preset');
          return;
        }
      }

      toast.success('User updated successfully');
      setRoleChangeModalOpen(false);
      selectUser(null);
      await reloadUsers();
    } catch (error) {
      captureAndReportError(error, {
        operation: 'superadmin.updateUserRole',
        component: 'UserManagementPage',
        userId: selectedUser.id,
        newRole,
      });
      toast.error('Failed to update role');
    }
  };

  const handleStatusToggle = async (
    userId: string,
    action: 'suspend' | 'reactivate',
    targetUserRole: UserRole,
  ) => {
    if (!canModifyUserRole(targetUserRole, viewerRole)) {
      toast.error("You cannot modify this user's role");
      return;
    }

    try {
      const result = await trpc.client.toggleUserStatus.mutate({
        userId,
        action,
      });

      if (isRightResult(result)) {
        toast.success(result.right.message);
        await reloadUsers();
      } else if (isLeftResult(result)) {
        toast.error(result.left.message);
      }
    } catch (error) {
      captureAndReportError(error, {
        operation: `superadmin.${action}User`,
        component: 'UserManagementPage',
        userId,
      });
      toast.error(`Failed to ${action} user`);
    }
  };

  const columns = [
    {
      key: 'user',
      header: 'User',
      headerClassName: 'text-xs font-semibold text-neutral-300',
      size: 250,
      enableSorting: false,
      accessor: (user: UserDetail) => (
        <div className="space-y-0.5">
          <div className="text-sm font-medium text-white truncate">
            {user.fullName}
          </div>
          <div className="text-xs text-neutral-400 truncate">{user.email}</div>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      headerClassName: 'text-xs font-semibold text-neutral-300',
      size: 120,
      enableSorting: false,
      accessor: (user: UserDetail) => (
        <span
          className={`px-2 py-1 rounded text-xs font-medium border capitalize ${getRoleBadgeColor(user.role)}`}
        >
          {user.role}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      headerClassName: 'text-xs font-semibold text-neutral-300',
      size: 150,
      enableSorting: false,
      accessor: (user: UserDetail) => (
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5">
            {user.emailVerified ? (
              <CheckCircleIcon className="fill-current text-green-400 size-3.5" />
            ) : (
              <CloseIcon className="fill-current text-neutral-500 size-3.5" />
            )}
            <span
              className={`text-xs ${user.emailVerified ? 'text-green-400' : 'text-neutral-500'}`}
            >
              {user.emailVerified ? 'Verified' : 'Unverified'}
            </span>
          </div>
          <div className="text-[10px] text-neutral-500">
            {user.onboardingState.replace(/([A-Z])/g, ' $1').trim()}
          </div>
        </div>
      ),
    },
    {
      key: 'kybStatus',
      header: 'KYB Status',
      headerClassName: 'text-xs font-semibold text-neutral-300',
      size: 140,
      enableSorting: false,
      accessor: (user: UserDetail) => {
        if (!user.kybStatus) {
          return <span className="text-neutral-500 text-xs">{'—'}</span>;
        }

        return (
          <span
            className={`px-2 py-1 rounded text-xs font-medium border ${getKybStatusBadgeColor(user.kybStatus)}`}
          >
            {formatKybStatus(user.kybStatus)}
          </span>
        );
      },
    },
    {
      key: 'publisher',
      header: 'Publisher',
      headerClassName: 'text-xs font-semibold text-neutral-300',
      size: 200,
      enableSorting: false,
      accessor: (user: UserDetail) =>
        user.publisherName ? (
          <div className="space-y-0.5">
            <div className="text-sm text-white font-medium truncate">
              {user.publisherName}
            </div>
            <div className="text-xs text-neutral-400">
              {user.publisherType} • {user.dappCount} dapps
            </div>
          </div>
        ) : (
          <span className="text-neutral-500 text-xs">{'—'}</span>
        ),
    },
    {
      key: 'joined',
      header: 'Joined',
      headerClassName: 'text-xs font-semibold text-neutral-300',
      size: 140,
      enableSorting: true,
      sortDescFirst: true,
      accessor: (user: UserDetail) => (
        <div className="space-y-0.5">
          <div className="text-xs text-neutral-400">
            {formatRelativeTime(user.created)}
          </div>
          <div className="text-xs text-neutral-500">
            {formatDateTimeWithLocalZone(user.created)}
          </div>
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      headerClassName: 'text-xs font-semibold text-neutral-300',
      size: 100,
      enableSorting: false,
      accessor: (user: UserDetail) => {
        const canModifyUser = canModifyUserRole(user.role, viewerRole);

        return (
          <div className="flex items-center gap-2">
            {canModifyUser && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  selectUser(user.id);
                  setRoleChangeModalOpen(true);
                }}
                className="text-teal-400 hover:text-teal-300 transition-colors"
                title="Change role"
              >
                <EditIcon className="fill-current size-4" />
              </button>
            )}
            {canModifyUser && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleStatusToggle(user.id, 'suspend', user.role);
                }}
                className="text-red-400 hover:text-red-300 transition-colors"
                title="Suspend user"
              >
                <BlockIcon className="fill-current size-4" />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  if (!canRender) {
    return null;
  }

  return (
    <>
      <ManagementPageLayout
        authState={authState}
        canAccess={canAccessAdminPages}
      >
        <div className="space-y-6">
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold text-white">
              User Management
            </h1>
            <p className="text-sm text-neutral-400">
              View and manage all users in the system
            </p>
          </div>

          <FilterBar
            searchValue={filters.search}
            onSearchChange={(value) => updateFilter('search', value)}
            searchPlaceholder="Search by email or name..."
          >
            <SelectFilter
              value={filters.role}
              onChange={(value) => updateFilter('role', value)}
              options={[
                { value: 'all', label: 'All Roles' },
                { value: 'base', label: 'Base Users' },
                { value: 'moderator', label: 'Moderators' },
                { value: 'admin', label: 'Admins' },
                { value: 'superadmin', label: 'Superadmins' },
              ]}
            />
            <SelectFilter
              value={filters.onboardingState}
              onChange={(value) => updateFilter('onboardingState', value)}
              options={[
                { value: 'all', label: 'All States' },
                { value: 'EmailVerification', label: 'Email Verification' },
                { value: 'ProfileCreation', label: 'Profile Creation' },
                { value: 'KYB', label: 'KYB' },
                { value: 'Complete', label: 'Complete' },
              ]}
            />
            <SelectFilter
              value={filters.emailVerified}
              onChange={(value) => updateFilter('emailVerified', value)}
              options={[
                { value: 'all', label: 'All Users' },
                { value: 'true', label: 'Verified' },
                { value: 'false', label: 'Unverified' },
              ]}
            />
          </FilterBar>

          <div className="bg-neutral-800/50 rounded-lg border border-neutral-700/50 overflow-hidden shadow-lg">
            <div className="px-6 py-4 border-b border-neutral-700/50">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <h3 className="text-base font-semibold text-white">
                    {pagination.total}{' '}
                    {pagination.total === 1 ? 'User' : 'Users'}
                  </h3>
                  <p className="text-xs text-neutral-400">
                    Manage user accounts, roles, and permissions
                  </p>
                </div>
                {pagination.totalPages > 1 && (
                  <div className="text-xs text-neutral-500">
                    Page {pagination.currentPage} of {pagination.totalPages}
                  </div>
                )}
              </div>
            </div>

            <DataTable
              data={users}
              columns={columns}
              isLoading={isLoading}
              emptyMessage="No users found"
              enableSorting={true}
              sorting={sorting}
              onSortingChange={(updater) => {
                setSorting(updater);
              }}
              renderMobileItem={(user) => (
                <div className="space-y-3">
                  <div className="flex justify-between items-start">
                    <div className="space-y-0.5">
                      <div className="text-sm font-medium text-white">
                        {user.fullName}
                      </div>
                      <div className="text-xs text-neutral-400">
                        {user.email}
                      </div>
                    </div>
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium border capitalize ${
                        user.role === 'superadmin'
                          ? 'bg-purple-500/10 text-purple-400 border-purple-500/20'
                          : user.role === 'admin'
                            ? 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                            : user.role === 'moderator'
                              ? 'bg-teal-500/10 text-teal-400 border-teal-500/20'
                              : 'bg-neutral-500/10 text-neutral-400 border-neutral-500/20'
                      }`}
                    >
                      {user.role}
                    </span>
                  </div>

                  <div className="flex items-center gap-3 text-xs">
                    <div className="flex items-center gap-1.5">
                      {user.emailVerified ? (
                        <CheckCircleIcon className="fill-current text-green-400 size-3.5" />
                      ) : (
                        <CloseIcon className="fill-current text-neutral-500 size-3.5" />
                      )}
                      <span
                        className={
                          user.emailVerified
                            ? 'text-green-400'
                            : 'text-neutral-500'
                        }
                      >
                        {user.emailVerified ? 'Verified' : 'Unverified'}
                      </span>
                    </div>
                    {user.kybStatus && (
                      <span
                        className={`px-1.5 py-0.5 rounded border ${getKybStatusBadgeColor(user.kybStatus)}`}
                      >
                        {formatKybStatus(user.kybStatus)}
                      </span>
                    )}
                  </div>

                  {user.publisherName && (
                    <div className="text-xs text-neutral-400">
                      Publisher:{' '}
                      <span className="text-neutral-300">
                        {user.publisherName}
                      </span>
                    </div>
                  )}

                  {canModifyUserRole(user.role, viewerRole) && (
                    <div className="pt-2 border-t border-neutral-700/50 flex justify-end gap-3">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          selectUser(user.id);
                          setRoleChangeModalOpen(true);
                        }}
                        className="text-teal-400 text-xs font-medium hover:text-teal-300 flex items-center gap-1"
                      >
                        <EditIcon className="size-3.5 fill-current" />
                        Edit Role
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStatusToggle(user.id, 'suspend', user.role);
                        }}
                        className="text-red-400 text-xs font-medium hover:text-red-300 flex items-center gap-1"
                      >
                        <BlockIcon className="size-3.5 fill-current" />
                        Suspend
                      </button>
                    </div>
                  )}
                </div>
              )}
            />

            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={setPage}
            />
          </div>
        </div>
      </ManagementPageLayout>

      <RoleChangeModal
        user={selectedUser}
        isOpen={roleChangeModalOpen}
        onClose={() => {
          setRoleChangeModalOpen(false);
          selectUser(null);
        }}
        onConfirm={handleRoleUpdate}
        viewerRole={viewerRole}
        presets={presets}
      />
    </>
  );
}
